import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell } from '@gravity-ui/icons';
import { useNotificationSummary } from '../../hooks/useNotificationSummary';
import { formatUnreadBadge } from '../../utils/notificationSummary';
import { formatInTimezone } from '../../utils/formatInTimezone';
import { useCameraNames } from '../../hooks/useCameraNames';
import './NotificationBell.css';

/**
 * Колокольчик уведомлений (Bell Addendum v1.1).
 *
 * Это не отдельный центр уведомлений: popover показывает последние события и
 * уводит в существующую карточку /violations/{event_id}. Автопереходов при
 * открытии нет — пользователь сам выбирает, куда идти.
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const { state, unreadCount, items, marking, markError, refresh, markAllSeen } = useNotificationSummary();
  const [open, setOpen] = useState(false);
  // ТЗ №2 §10.3: время события — в часовом поясе камеры. Если summary не несёт
  // display_timezone, зона подтягивается по camera_id через общий кэш камер.
  const cameraItems = useMemo(
    () => items.filter((item) => item.cameraId && !item.displayTimezone).map((item) => ({ camera_id: item.cameraId })),
    [items]
  );
  const { cameraTimezone } = useCameraNames(cameraItems);
  const itemTimezone = (item) => item.displayTimezone || cameraTimezone(item.cameraId);
  const holderRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      // Закрываем popover, если клик вне кнопки AND вне самого popover
      if (
        holderRef.current
        && !holderRef.current.contains(event.target)
        && popoverRef.current
        && !popoverRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Эндпоинта нет — колокольчика тоже нет.
  if (state === 'unsupported') return null;

  const badge = formatUnreadBadge(unreadCount);

  const openNotification = (item) => {
    setOpen(false);
    navigate(item.url || '/violations', { state: { origin: 'bell' } });
  };

  const renderList = () => {
    if (state === 'loading') return <div className="nb-state">Загрузка…</div>;

    if (state === 'error') {
      return (
        <div className="nb-state">
          Не удалось загрузить уведомления
          <button type="button" className="nb-retry" onClick={refresh}>Повторить</button>
        </div>
      );
    }

    if (items.length === 0) return <div className="nb-state">Новых уведомлений нет</div>;

    return (
      <ul className="nb-list">
        {items.slice(0, 10).map((item, index) => (
          <li key={`${item.eventId ?? 'x'}-${index}`}>
            <button
              type="button"
              className={`nb-item${item.unread ? ' nb-item--unread' : ''}`}
              onClick={() => openNotification(item)}
            >
              <span className="nb-item-title">{item.title}</span>
              {/* Bell Addendum v1.1: отдельного body бэкенд не отдаёт,
                  подзаголовок — hierarchy_path. */}
              {item.path && <span className="nb-item-path">{item.path}</span>}
              {item.confirmedAt && (
                <span className="nb-item-time">{formatInTimezone(item.confirmedAt, itemTimezone(item))}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="nb" ref={holderRef}>
      <button
        type="button"
        className="nb-button"
        aria-label="Уведомления"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Bell width={18} height={18} />
        {badge && <span className="nb-badge" data-testid="bell-badge">{badge}</span>}
      </button>

      {open && createPortal(
        <div className="nb-popover" role="dialog" aria-label="Последние уведомления" ref={popoverRef}>
          <div className="nb-popover-head">
            <span className="nb-popover-title">Уведомления</span>
            <button
              type="button"
              className="nb-linkbtn"
              disabled={marking || unreadCount === 0}
              onClick={markAllSeen}
            >
              Отметить всё прочитанным
            </button>
          </div>

          {renderList()}

          {markError && <div className="nb-mark-error">{markError}</div>}

          <div className="nb-popover-foot">
            <button
              type="button"
              className="nb-linkbtn"
              onClick={() => { setOpen(false); navigate('/violations'); }}
            >
              Показать все
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
